import { type Component, For, type JSX } from "solid-js";

type Accent = "mint" | "blue" | "amber" | "pink" | "mute";

function cx(...parts: Array<string | undefined | false>): string {
  return parts.filter(Boolean).join(" ");
}

export const PulseDot: Component<{
  accent?: Accent;
  live?: boolean;
  class?: string;
}> = (props) => (
  <span
    aria-hidden="true"
    class={cx(
      "op-pulse-dot",
      `op-pulse-dot-${props.accent ?? "mint"}`,
      props.live === false ? "" : "op-pulse-dot-live",
      props.class,
    )}
  />
);

export const Kbd: Component<{ children: JSX.Element }> = (props) => (
  <span class="op-kbd">{props.children}</span>
);

export const StatusPill: Component<{
  children: JSX.Element;
  tone?: "ok" | "degraded" | "down" | "idle";
  live?: boolean;
  accent?: Accent;
}> = (props) => (
  <span class={cx("op-status-pill", `op-status-pill-${props.tone ?? "idle"}`)}>
    {props.live && <PulseDot accent={props.accent} />}
    {props.children}
  </span>
);

export const StatCell: Component<{
  label: string;
  value: JSX.Element;
  unit?: JSX.Element;
  delta?: JSX.Element;
  tone?: "ok" | "warn" | "muted";
}> = (props) => (
  <div class="op-stat-cell">
    <div class="op-stat-label">{props.label}</div>
    <div class="op-stat-value">
      <span>{props.value}</span>
      {props.unit && <span class="op-stat-unit">{props.unit}</span>}
      {props.delta && (
        <span
          class={cx("op-stat-delta", `op-stat-delta-${props.tone ?? "ok"}`)}
        >
          {props.delta}
        </span>
      )}
    </div>
  </div>
);

export const Sparkline: Component<{
  data: number[];
  accent?: Accent;
  label?: string;
}> = (props) => {
  const points = () => {
    const data = props.data.length > 0 ? props.data : [0];
    const max = Math.max(...data);
    const min = Math.min(...data);
    const span = max - min || 1;
    return data.map((v, i) => {
      const x = data.length === 1 ? 50 : (i / (data.length - 1)) * 100;
      const y = 100 - ((v - min) / span) * 88 - 6;
      return [x, y] as const;
    });
  };

  const path = () =>
    points()
      .map(
        ([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`,
      )
      .join(" ");

  const last = () => points()[points().length - 1] ?? [0, 94];

  return (
    <svg
      class={cx("op-sparkline", `op-sparkline-${props.accent ?? "mint"}`)}
      viewBox="0 0 100 100"
      role={props.label ? "img" : "presentation"}
      aria-label={props.label}
      preserveAspectRatio="none"
    >
      <For each={[25, 50, 75]}>
        {(y) => (
          <line x1="0" x2="100" y1={y} y2={y} class="op-sparkline-grid" />
        )}
      </For>
      <path d={`${path()} L100,100 L0,100 Z`} class="op-sparkline-area" />
      <path d={path()} class="op-sparkline-line" />
      <circle cx={last()[0]} cy={last()[1]} r="2.8" class="op-sparkline-dot" />
    </svg>
  );
};
